import React from 'react';
import { CheckCircle2, Percent, Timer, ShieldCheck, ArrowRight, PlayCircle } from 'lucide-react';
import { Button } from './ui/button';

export const AvanceImmediate = () => {
  return (
    <section
      id="avance-immediate"
      className="relative py-20 overflow-hidden bg-gradient-to-br from-[#0F172A] via-[#1E4585] to-[#2B5FAC]"
      data-testid="avance-immediate-section"
    >
      {/* Décors */}
      <div className="absolute -top-32 -right-32 w-[500px] h-[500px] rounded-full bg-[#FFCC00] opacity-20 pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-[420px] h-[420px] rounded-full bg-[#5BB628] opacity-15 pointer-events-none" />
      <div className="absolute top-24 right-40 w-5 h-5 rounded-full bg-[#5BB628]" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        {/* Badge Urssaf */}
        <div className="flex flex-wrap items-center gap-3 mb-6">
          <span className="inline-block bg-[#5BB628] text-white font-black text-sm sm:text-base px-4 py-2 tracking-wide">
            NOUVEAU &middot; SERVICE URSSAF
          </span>
          <span className="inline-flex items-center gap-2 bg-white pl-1.5 pr-3 py-1 rounded-lg shadow-md" data-testid="sap-badge">
            <img src="/sap-logo.png" alt="Services à la personne agréé" className="h-9 w-auto" />
            <span className="text-[#0F172A] font-black text-xs sm:text-sm leading-tight">
              AGRÉÉ<br /><span className="text-[#F0BC00]">SAP 999 770 779</span>
            </span>
          </span>
        </div>

        {/* Title */}
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <div className="text-white">
            <h2 className="text-4xl sm:text-5xl lg:text-6xl font-black leading-none tracking-tight break-words">
              Payez <span className="relative inline-block">
                <span className="line-through decoration-[#FFCC00] decoration-8 decoration-[-2deg]">100&nbsp;%</span>
              </span>{' '}
              <span className="block mt-2">de mes prestations&nbsp;?</span>
              <span
                className="block mt-3 text-[#FFCC00] leading-tight"
                style={{ fontFamily: "'Caveat', cursive", fontSize: '1.3em', transform: 'rotate(-2deg)' }}
              >
                Plus maintenant&nbsp;!
              </span>
            </h2>

            <p className="mt-6 text-lg text-[#D4F0BC] leading-relaxed">
              Grâce à <strong className="text-white">l'Avance immédiate de crédit d'impôt</strong> de
              l'Urssaf, vous déduisez <strong className="text-[#FFCC00]">50 %</strong> de ma facture
              directement au moment du paiement. <strong className="text-white">Gratuit</strong>,{' '}
              <strong className="text-white">optionnel</strong>, <strong className="text-white">immédiat</strong>.
            </p>

            <div className="mt-6 inline-flex flex-wrap items-center gap-3 sm:gap-4 bg-white/10 backdrop-blur-sm rounded-xl px-4 sm:px-5 py-4 border border-white/10">
              <div className="text-left sm:text-right">
                <div className="text-xs uppercase tracking-wider text-[#D4F0BC] font-semibold">Exemple</div>
                <div className="text-white mt-1">Prestation&nbsp;:</div>
              </div>
              <div className="text-2xl sm:text-4xl font-black text-white/60 line-through">200&nbsp;€</div>
              <ArrowRight className="w-5 h-5 sm:w-6 sm:h-6 text-[#FFCC00]" />
              <div className="text-3xl sm:text-5xl font-black text-[#FFCC00]">100&nbsp;€</div>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              <a
                href="#contact"
                onClick={(e) => { e.preventDefault(); document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' }); }}
                data-testid="ai-contact-btn"
              >
                <Button className="bg-[#FFCC00] hover:bg-[#F0BC00] text-[#0F172A] font-bold px-6 py-6 text-base rounded-lg shadow-xl">
                  En profiter pour mes travaux
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </a>
              <a
                href="/DMS_Pack_Avance_Immediate.zip"
                download
                data-testid="ai-pack-download-btn"
              >
                <Button variant="outline" className="bg-transparent border-2 border-white/60 text-white hover:bg-white hover:text-[#0F172A] font-bold px-6 py-6 text-base rounded-lg">
                  Télécharger les infos (PDF)
                </Button>
              </a>
            </div>
          </div>

          {/* Steps card */}
          <div className="relative">
            <div className="bg-white rounded-2xl shadow-2xl p-6 sm:p-8">
              <h3 className="text-2xl font-black text-[#0F172A] mb-6 leading-tight">
                Comment ça marche&nbsp;?
              </h3>

              <ol className="space-y-4">
                {[
                  { n: '01', t: 'Je vous ouvre un compte Urssaf (avec votre accord).', color: '#FFCC00' },
                  { n: '02', t: 'Vous activez votre compte via un mail de l\'Urssaf.', color: '#5BB628' },
                  { n: '03', t: 'À chaque prestation, je transmets la demande avec 50 % déjà déduits.', color: '#2B5FAC' },
                  { n: '04', t: 'L\'Urssaf prélève votre reste à charge à J+2.', color: '#5BB628' },
                  { n: '05', t: 'Votre déclaration d\'impôts est pré-remplie automatiquement.', color: '#FFCC00' },
                ].map((s) => {
                  const isLight = s.color === '#FFCC00';
                  return (
                    <li key={s.n} className="flex items-start gap-4" data-testid={`ai-step-${s.n}`}>
                      <span
                        className="flex-shrink-0 w-11 h-11 rounded-full font-black flex items-center justify-center text-sm shadow-md"
                        style={{ backgroundColor: s.color, color: isLight ? '#0F172A' : '#ffffff' }}
                      >
                        {s.n}
                      </span>
                      <span className="pt-2 text-[#0F172A] leading-snug">{s.t}</span>
                    </li>
                  );
                })}
              </ol>

              <div className="mt-6 pt-5 border-t border-gray-200 grid grid-cols-3 gap-3 text-center">
                <div>
                  <Percent className="w-6 h-6 text-[#F0BC00] mx-auto" />
                  <div className="text-xs font-semibold text-gray-600 mt-1">50 % déduits</div>
                </div>
                <div>
                  <Timer className="w-6 h-6 text-[#F0BC00] mx-auto" />
                  <div className="text-xs font-semibold text-gray-600 mt-1">Immédiat</div>
                </div>
                <div>
                  <ShieldCheck className="w-6 h-6 text-[#F0BC00] mx-auto" />
                  <div className="text-xs font-semibold text-gray-600 mt-1">100 % sécurisé</div>
                </div>
              </div>
            </div>

            {/* Pastille flottante */}
            <div className="hidden sm:flex absolute -top-5 -right-5 w-24 h-24 rounded-full bg-[#FFCC00] text-[#0F172A] font-black text-center items-center justify-center text-sm leading-tight shadow-xl rotate-[-8deg] border-4 border-white">
              Service<br />GRATUIT
            </div>
          </div>
        </div>

        {/* Vidéo officielle Urssaf */}
        <div className="mt-14 relative" data-testid="ai-video-block">
          <div className="text-center mb-5">
            <div className="inline-flex items-center gap-2 text-[#FFCC00] font-semibold text-sm uppercase tracking-wider">
              <PlayCircle className="w-5 h-5" />
              Vidéo de présentation · 1 min 41
            </div>
            <h3 className="mt-2 text-2xl sm:text-3xl font-black text-white leading-tight">
              Tout comprendre en moins de 2 minutes
            </h3>
          </div>
          <div className="max-w-3xl mx-auto rounded-2xl overflow-hidden shadow-2xl ring-1 ring-white/10 bg-black">
            <video
              controls
              preload="metadata"
              poster="/video/poster-avance-immediate.jpg"
              className="w-full h-auto block"
              data-testid="ai-video-player"
            >
              <source src="/video/avance-immediate-720p.mp4" type="video/mp4" />
              Votre navigateur ne prend pas en charge la lecture vidéo.
            </video>
          </div>
          <p className="mt-3 text-center text-xs text-[#D4F0BC]">
            Source&nbsp;: Urssaf &middot; motion design officiel du service Avance immédiate.
          </p>
        </div>

        {/* Éligibilité */}
        <div className="mt-12 grid md:grid-cols-3 gap-4">
          {[
            'Bricolage (petits travaux "homme toutes mains")',
            'Jardinage chez un particulier',
            'Assistance informatique à domicile',
          ].map((a) => (
            <div key={a} className="flex items-center gap-3 bg-white/5 backdrop-blur-sm rounded-lg px-4 py-3 border border-white/10">
              <CheckCircle2 className="w-5 h-5 text-[#FFCC00] flex-shrink-0" />
              <span className="text-white text-sm font-medium">{a}</span>
            </div>
          ))}
        </div>

        <p className="mt-8 text-xs text-[#D4F0BC] max-w-4xl">
          Service géré par l'Urssaf. Conditions&nbsp;: être fiscalement domicilié en France et avoir
          déjà effectué au moins une déclaration de revenus. DMS David Multi Service est déclaré
          Services à la Personne sous le n° SAP&nbsp;999&nbsp;770&nbsp;779.
        </p>
      </div>
    </section>
  );
};
